import type { ContentHierarchyNode, ContentHierarchyResponse } from '@/api/admin'
import {
  buildPrintBookTitle,
  type PrintBookBulkCsvError,
  type PrintBookBulkCsvParseResult,
} from './printBookBulkCsv'

export interface PrintBookManualDraft {
  id: string
  volumeLabel: string
  title: string
  moduleIds: number[]
}

export interface PrintBookModuleOption {
  id: number
  title: string
  parentPath: string[]
}

interface BuildManualRowsOptions {
  bookLabel: string
  titlePattern: string
}

type HierarchyItem = ContentHierarchyNode | ContentHierarchyResponse

export function buildPrintBookManualRows(
  drafts: PrintBookManualDraft[],
  options: BuildManualRowsOptions,
): PrintBookBulkCsvParseResult {
  const errors: PrintBookBulkCsvError[] = []

  const rows = drafts.flatMap((draft, index) => {
    const rowNumber = index + 1
    const volumeLabel = draft.volumeLabel.trim()
    const rawTitle = draft.title.trim()

    if (!volumeLabel) {
      errors.push({
        rowNumber,
        code: 'VOLUME_REQUIRED',
        message: '권 표기를 입력해 주세요.',
      })
      return []
    }

    return [{
      rowNumber,
      orderIndex: rowNumber,
      volumeLabel,
      rawTitle,
      title: rawTitle || buildPrintBookTitle(options.titlePattern, options.bookLabel, volumeLabel),
      moduleIds: draft.moduleIds,
    }]
  })

  return {
    rows,
    errors,
    hasErrors: errors.length > 0,
  }
}

export function collectDescendantModules(root: HierarchyItem): PrintBookModuleOption[] {
  const modules: PrintBookModuleOption[] = []

  function visit(item: HierarchyItem, parentPath: string[]) {
    if (item.type === 'module') {
      modules.push({
        id: item.id,
        title: item.title,
        parentPath,
      })
      return
    }

    const children = [...item.children].sort((left, right) => {
      const leftOrder = 'sequenceOrder' in left ? left.sequenceOrder : 0
      const rightOrder = 'sequenceOrder' in right ? right.sequenceOrder : 0
      return leftOrder - rightOrder
    })

    children.forEach((child) => visit(child, [...parentPath, item.title]))
  }

  visit(root, [])
  return modules
}
